import { motion } from "framer-motion";
import { Heartbeat, Calculator, NotePencil, DotsThreeOutline } from "@phosphor-icons/react";

const BUCKETS = [
  {
    id: "T-01",
    title: "In the code",
    desc: "When seconds matter.",
    icon: <Heartbeat size={22} weight="duotone" />,
    tools: [
      "Code recorder",
      "EKG w/ clinical context",
      "Scoring calculators",
    ],
  },
  {
    id: "T-02",
    title: "Bedside calculators",
    desc: "The math, done right.",
    icon: <Calculator size={22} weight="duotone" />,
    tools: [
      "Dosing tools",
      "Y-site checker",
      "Drug interaction checker",
    ],
  },
  {
    id: "T-03",
    title: "After the shift",
    desc: "Document, learn, grow.",
    icon: <NotePencil size={22} weight="duotone" />,
    tools: [
      "AI-powered SBAR builder",
      "Education handout creator",
      "CE & certification tracker",
    ],
  },
];

export default function Toolkit() {
  return (
    <section
      id="toolkit"
      data-testid="toolkit-section"
      className="relative py-28 md:py-36 bg-black border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-4">
              § 02 · the toolkit
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.95]">
              More than
              <br />
              <span className="italic font-light">a chat.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-base text-white/55 leading-relaxed self-end">
            A growing kit of purpose-built tools, organized around the way a
            shift actually unfolds — not the way a textbook reads.
          </p>
        </div>

        {/* Buckets */}
        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {BUCKETS.map((b, i) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative bg-[#050505] p-8 md:p-10 hover:bg-black transition-colors flex flex-col"
              data-testid={`toolkit-bucket-${b.id}`}
            >
              {/* Header row */}
              <div className="flex items-start justify-between mb-6">
                <div className="w-10 h-10 grid place-items-center bg-[#FF2A2A]/10 border border-[#FF2A2A]/40 text-[#FF2A2A]">
                  {b.icon}
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/35">
                  {b.id}
                </span>
              </div>

              {/* Title + desc */}
              <h3 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight leading-tight">
                {b.title}
              </h3>
              <p className="mt-2 text-sm text-white/45 font-mono uppercase tracking-[0.18em] text-[10px]">
                {b.desc}
              </p>

              {/* Tool pills */}
              <ul className="mt-7 flex flex-wrap gap-2">
                {b.tools.map((t) => (
                  <li
                    key={t}
                    className="px-3 py-1.5 border border-white/15 bg-white/[0.03] font-mono text-[11px] uppercase tracking-[0.14em] text-white/80 hover:border-[#FF2A2A]/60 hover:text-white transition-colors"
                  >
                    {t}
                  </li>
                ))}
              </ul>

              <div
                className="absolute bottom-0 left-0 right-0 h-px bg-[#FF2A2A] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                aria-hidden="true"
              />
            </motion.div>
          ))}
        </div>

        {/* + much more */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="mt-8 flex items-center justify-center md:justify-end gap-3"
          data-testid="toolkit-much-more"
        >
          <div className="hidden md:block h-px w-12 bg-white/15" />
          <span className="inline-flex items-center gap-2 px-4 py-2 border border-[#FF2A2A]/40 bg-[#FF2A2A]/[0.06] font-mono text-[10px] uppercase tracking-[0.24em] text-[#FF2A2A]">
            <DotsThreeOutline size={14} weight="fill" />
            + much more
          </span>
        </motion.div>
      </div>
    </section>
  );
}
